/**
 * T3C SDK 事件监听器注册工具
 * @description 统一管理 T3C SDK 的事件监听器注册逻辑，避免重复代码
 */

import { message } from 'antd'
import { t3cStore } from './t3c-store'
import { sessionStore } from './session-store'
import { moduleId } from '@configs'
import { LogType } from '@tencent/aegis-web-sdk-v2'
import { getT3CCallInRecordCallerBySessionId } from '@services/t3c'

/**
 * 注册 T3C SDK 的基础事件监听器
 * 包括 ready、tokenExpired、callOuted、calloutAccepted、muted、asr、kickedOut 等
 */
export const registerT3cBaseListeners = () => {
  const {
    setCurTabUUID,
    setT3cReady,
    setT3cFailed,
    setT3cErrorMsg,
    setCallStatus,
    setSessionId,
    setCallInfo,
    setShowCallWorkbench,
    setIsCallMute,
    handleAsrEvent,
  } = t3cStore.getState()

  // ready 事件
  window.tccc.on(window.tccc.events.ready, ({ tabUUID }) => {
    setCurTabUUID(tabUUID)
    setT3cReady(true)
    setT3cFailed(false)
    setT3cErrorMsg('')
    console.log('T3C SDK 初始化成功，当前客服状态：', window.tccc.Agent.getStatus(), 'tabUUID:', tabUUID)
  })

  // Token 过期事件
  window.tccc.on(window.tccc.events.tokenExpired, ({ message: errMsg, tabUUID }) => {
    console.error('初始化失败:', errMsg)
    const { curTabUUID } = t3cStore.getState()
    const { userInfo } = sessionStore.getState()
    message.error(`TCCC error: ${errMsg}。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
    setT3cErrorMsg(errMsg)
    setT3cReady(true)
    setT3cFailed(true)
    console.log('T3C SDK 初始化失败. tabUUID:', tabUUID)
  })

  // 外呼成功事件
  window.tccc.on(window.tccc.events.callOuted, (res: any) => {
    setCallStatus('calling')
    console.log('外呼成功事件 window.tccc.events.callOuted', res)
    setSessionId(res.sessionId)
    setCallInfo((draft) => {
      draft.calleeLocation = res.calleeLocation
      draft.calleePhoneNumberText = res.calleePhoneNumberText
      draft.callerPhoneNumberText = res.callerPhoneNumberText
      draft.callTimestamp = 0
    })
    setShowCallWorkbench(true)
  })

  // 外呼对方接听事件
  window.tccc.on(window.tccc.events.calloutAccepted, (res: any) => {
    setCallStatus('talking')
    console.log('外呼对方接听事件 window.tccc.events.calloutAccepted', res)
    setSessionId(res.sessionId)
    setShowCallWorkbench(true)
    setCallInfo((draft) => {
      draft.callTimestamp = res.timestamp
    })
  })

  // 静音事件
  window.tccc.on('muted', () => {
    setIsCallMute(true)
  })

  // 语音识别事件
  window.tccc.on(window.tccc.events.asr, handleAsrEvent)

  // 踢出事件
  try {
    window.tccc.on(window.tccc.events.kickedOut, () => {
      const { curTabUUID } = t3cStore.getState()
      const { userInfo } = sessionStore.getState()
      message.error(`当前坐席在其他地方登录。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
      console.log('当前坐席在其他地方登录')
    })
  } catch (error) {
    console.log('T3C SDK kickedOut error:', error)
  }
}

/**
 * 注册 T3C SDK 的扩展事件监听器
 * 包括 userAccessed、sessionEnded 等需要在 t3cReady 后注册的事件
 * 这些事件依赖于用户权限、400热线配置等信息
 */
export const registerT3cExtendedListeners = () => {
  const { isPermissableBelongModule, userInfo } = sessionStore.getState()
  const { hotline400Config, curTabUUID, resetCallState, getTodayCallDuration } = t3cStore.getState()

  // 隐藏通知栏（SDR清洗模块且使用新通话条）
  if (isPermissableBelongModule([moduleId.SDR_CLEAN]) && userInfo.useNewT3CCallBar) {
    window.tccc.UI.hideNotificationBar()
  }

  // 来电显示事件
  const handleUserAccessed = async (res: any) => {
    console.log('来电显示', res, 'hotline400Config:', hotline400Config)
    const result = hotline400Config?.list.find(item => item.hotline === res.calleePhoneNumber)
    window.AegisV2?.reportEvent({
      name: 'tccc_user_accessed',
      type: 'custom_event' as LogType,
      msg: `来电显示result: ${JSON.stringify(result)}, res.tabUUID: ${res.tabUUID}, curTabUUID: ${curTabUUID}`,
      eventId: Date.now(),
      user: userInfo?.salesName,
    })
    // 如果来源是乐享或电子签，跳转到通用400
    if (res.tabUUID === curTabUUID && result && result.redirect === 1) {
      if (result.from === 'meeting') {
        // 腾讯会议来源，需要通过sessionId获取实际的来电号码
        let callerPhone = res.callerPhoneNumber
        const [data, err] = await getT3CCallInRecordCallerBySessionId({ sessionId: res.sessionId })
        if (err) {
          message.error(`获取来电号码失败:${err?.message}`)
        } else {
          callerPhone = data?.caller
        }
        window.open(`/lead/hotline/call-success?qd_tel=${callerPhone}&qd_calll=t3c-${res.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
      } else {
        window.open(`/lead/hotline/clue-collect/400?qd_tel=${res.callerPhoneNumber}&qd_calll=t3c-${res.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
      }
    }
  }

  // 通话结束事件
  const handleSessionEnded = () => {
    console.log('通话结束')
    resetCallState()
    // 延迟刷新通话时长
    setTimeout(() => {
      getTodayCallDuration()
    }, 2000)
    setTimeout(() => {
      getTodayCallDuration()
    }, 30000)
  }

  window.tccc.on(window.tccc.events.userAccessed, handleUserAccessed)
  window.tccc.on(window.tccc.events.sessionEnded, handleSessionEnded)
}
